import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Coins, Wallet, TrendingUp, TrendingDown, Store, BarChart3, Info, Users, ChevronLeft, ArrowLeftRight, Landmark } from "lucide-react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";

const PERIODS = [
    { key: "daily",   ar: "يومي",   en: "Daily" },
    { key: "weekly",  ar: "أسبوعي", en: "Weekly" },
    { key: "monthly", ar: "شهري",   en: "Monthly" },
    { key: "yearly",  ar: "سنوي",   en: "Yearly" },
];

const fmt = (v) => (Number.isFinite(v) ? v.toLocaleString("en-US", { maximumFractionDigits: 3 }) : "—");

export default function ShopLog() {
    const { company } = useAuth();
    if (company?.mansori_manager) return <Navigate to="/mshop/shops" replace />;
    if (company?.mansori_shop) return <Navigate to="/mshop/shop-log" replace />;
    if (company?.account_type === "employee") return <Navigate to="/" replace />;
    // Shop log is driven by the daily ledger — hidden when the account has no daily.
    if (company && company.account_type !== "manager" && !(company.enabled_ledgers || []).includes("daily")) return <Navigate to="/" replace />;
    return <ShopLogInner />;
}

function ShopLogInner() {
    const navigate = useNavigate();
    const { company, refresh } = useAuth();
    const { lang } = useLang();
    const [period, setPeriod] = useState("monthly");
    const [refDate, setRefDate] = useState(new Date().toISOString().slice(0, 10));
    const [data, setData] = useState(null);           // individual/employee: shop-log ; manager: aggregate
    const [loading, setLoading] = useState(true);
    const [askOpening, setAskOpening] = useState(false);
    // Manager drill-down: null = summary view, otherwise show a specific employee.
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [empDetail, setEmpDetail] = useState(null);
    const [empLoading, setEmpLoading] = useState(false);
    const isManager = company?.account_type === "manager";

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Managers get aggregate; individuals get their own. The backend
            // dispatches based on account_type, so we always hit the same
            // endpoint here.
            const r = await api.get("/shop-log", { params: { period, date: refDate } });
            setData(r.data);
            const self = isManager ? r.data.self : r.data;
            if (self && self.opening && self.opening.ever_set === false) {
                setAskOpening(true);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [period, refDate, isManager]);

    useEffect(() => { load(); }, [load]);

    // Fetch drilled-down employee detail whenever selection changes.
    useEffect(() => {
        if (!selectedEmployeeId) { setEmpDetail(null); return; }
        setEmpLoading(true);
        (async () => {
            try {
                const r = await api.get(`/shop-log/employee/${selectedEmployeeId}`, { params: { period, date: refDate } });
                setEmpDetail(r.data);
            } catch (e) { toast.error(formatApiError(e)); setSelectedEmployeeId(null); }
            finally { setEmpLoading(false); }
        })();
    }, [selectedEmployeeId, period, refDate]);

    // Legacy fallback for opening modal
    useEffect(() => {
        if (loading) return;
        const self = isManager ? data?.self : data;
        if (self && (self.opening.gold == null || self.opening.cash == null)) {
            setAskOpening(true);
        }
    }, [loading, data, isManager]);

    const displayLog = isManager ? (selectedEmployeeId ? empDetail : (data ? { ...data.total, variant: data.self?.variant, opening: data.self?.opening } : null)) : data;
    const showProfit = displayLog?.variant === "advanced" && displayLog?.profit != null;
    const showCF = displayLog?.has_clients_factories && displayLog?.clients_factories;

    return (
        <AppShell>
            <div className="mx-auto max-w-5xl" dir={lang === "ar" ? "rtl" : "ltr"}>
                <button onClick={() => selectedEmployeeId ? setSelectedEmployeeId(null) : navigate("/")} className="mb-3 inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900" data-testid="sl-back">
                    <ArrowRight className="h-4 w-4" /> {selectedEmployeeId ? (lang === "ar" ? "رجوع للإجمالي" : "Back to summary") : (lang === "ar" ? "الدفاتر" : "Ledgers")}
                </button>

                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">Meyar</p>
                        <h1 className="font-heading text-2xl font-bold sm:text-3xl">
                            {selectedEmployeeId ? `${lang === "ar" ? "سجل المحل — " : "Shop Log — "}${empDetail?.name || "…"}` : (lang === "ar" ? "سجل المحل" : "Shop Log")}
                        </h1>
                        <p className="mt-1 text-xs text-neutral-500 sm:text-sm">
                            {isManager && !selectedEmployeeId
                                ? (lang === "ar" ? "إجمالي الشركة (المدير + جميع الموظفين) مستخرج مباشرة من دفاتر الحركة" : "Company total (manager + all employees) pulled directly from daily ledgers")
                                : (lang === "ar" ? "لمحة تلقائية عن وضع المحل — الأرقام مستخرجة من دفاترك مباشرة" : "Automatic overview of your shop — numbers pulled from your ledgers")}
                        </p>
                    </div>
                </div>

                {/* Period + reference date */}
                <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-neutral-200 bg-white p-3">
                    <div className="flex items-center gap-1" data-testid="sl-period-tabs">
                        {PERIODS.map((p) => (
                            <button key={p.key} type="button" onClick={() => setPeriod(p.key)}
                                data-testid={`sl-period-${p.key}`}
                                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${period === p.key ? "bg-amber-700 text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
                                {lang === "ar" ? p.ar : p.en}
                            </button>
                        ))}
                    </div>
                    <div className="mr-auto flex items-center gap-2">
                        <Label className="text-[11px] font-bold text-neutral-500">{lang === "ar" ? "التاريخ المرجعي" : "Reference date"}</Label>
                        <Input type="date" value={refDate} onChange={(e) => setRefDate(e.target.value)} className="h-8 w-40 text-xs" data-testid="sl-ref-date" />
                    </div>
                </div>

                {loading || (selectedEmployeeId && empLoading) ? (
                    <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/50 p-10 text-center">
                        <Loader2 className="mx-auto h-6 w-6 animate-spin text-amber-700" />
                    </div>
                ) : !displayLog ? null : (
                    <div className="space-y-4">
                        {/* Manager summary heading */}
                        {isManager && !selectedEmployeeId ? (
                            <div className="rounded-2xl bg-amber-50 p-3 text-sm font-bold text-amber-900 ring-1 ring-amber-200" data-testid="sl-company-total-heading">
                                {lang === "ar" ? "إجمالي الشركة" : "Company total"}
                            </div>
                        ) : null}

                        {/* Point-in-time balances */}
                        <BalanceCard
                                icon={Coins} tone="amber"
                                label={lang === "ar" ? "وزن الذهب الحالي" : "Current gold weight"}
                                sub={displayLog.opening ? (lang === "ar" ? `الافتتاحي: ${fmt(displayLog.opening.gold)} جم` : `Opening: ${fmt(displayLog.opening.gold)} g`) : ""}
                                value={fmt(displayLog.current.gold)}
                                unit={lang === "ar" ? "جرام" : "grams"}
                                testId="sl-current-gold"
                            />
                        {/* Money split: each balance moves only with its own payment method */}
                        <div className="grid gap-3 sm:grid-cols-3" data-testid="sl-money-split">
                            <BalanceCard
                                icon={Wallet} tone="emerald"
                                label={lang === "ar" ? "الكاش" : "Cash"}
                                sub={displayLog.opening ? (lang === "ar" ? `الافتتاحي: ${fmt(displayLog.opening.cash)}` : `Opening: ${fmt(displayLog.opening.cash)}`) : ""}
                                value={fmt(displayLog.current.cash)}
                                unit=""
                                testId="sl-current-cash"
                            />
                            <BalanceCard
                                icon={ArrowLeftRight} tone="sky"
                                label={lang === "ar" ? "التحويل" : "Transfer"}
                                sub={lang === "ar" ? "يبدأ من صفر" : "Starts from zero"}
                                value={fmt(displayLog.current.transfer)}
                                unit=""
                                testId="sl-current-transfer"
                            />
                            <BalanceCard
                                icon={Landmark} tone="violet"
                                label={lang === "ar" ? "البنك" : "Bank"}
                                sub={lang === "ar" ? "يبدأ من صفر" : "Starts from zero"}
                                value={fmt(displayLog.current.bank)}
                                unit=""
                                testId="sl-current-bank"
                            />
                        </div>

                        {/* Period flows */}
                        <div className="rounded-2xl border border-neutral-200 bg-white p-4">
                            <div className="mb-3 flex items-center gap-2">
                                <BarChart3 className="h-4 w-4 text-neutral-500" />
                                <h2 className="font-heading text-sm font-bold text-neutral-800">
                                    {lang === "ar" ? `حركة الفترة (${data?.start || ""} → ${data?.end || ""})` : `Period movement (${data?.start || ""} → ${data?.end || ""})`}
                                </h2>
                            </div>
                            <div className={`grid gap-3 ${showProfit ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                                <FlowCard
                                    label={lang === "ar" ? "الذهب المباع" : "Gold sold"}
                                    value={fmt(displayLog.flows.sold_weight)} unit={lang === "ar" ? "جم" : "g"}
                                    icon={TrendingDown} tone="rose" testId="sl-sold-weight"
                                />
                                <FlowCard
                                    label={lang === "ar" ? "الذهب المشترى" : "Gold bought"}
                                    value={fmt(displayLog.flows.bought_weight)} unit={lang === "ar" ? "جم" : "g"}
                                    icon={TrendingUp} tone="emerald" testId="sl-bought-weight"
                                />
                                {showProfit ? (
                                    <FlowCard
                                        label={lang === "ar" ? "المكسب" : "Profit"}
                                        value={fmt(displayLog.profit)} unit=""
                                        icon={TrendingUp} tone="amber" testId="sl-profit"
                                    />
                                ) : null}
                            </div>
                        </div>

                        {/* Clients + Factories split — hidden when neither ledger enabled */}
                        {showCF ? (
                            <div className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid="sl-cf-section">
                                <div className="mb-3 flex items-center justify-between">
                                    <h2 className="font-heading text-sm font-bold text-neutral-800">
                                        {lang === "ar" ? "الذهب الصافي — العملاء والمصانع" : "Net gold — Clients & Factories"}
                                    </h2>
                                    <div className="hidden text-[10px] text-neutral-500 sm:block">
                                        {lang === "ar" ? "أخضر = علينا · أحمر = لنا" : "Green = we owe · Red = owed to us"}
                                    </div>
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <SplitCard tone="rose" label={lang === "ar" ? "لنا (أحمر / موجب)" : "Owed to us (red / positive)"}
                                        goldValue={fmt(displayLog.clients_factories.red_gold)} cashValue={fmt(displayLog.clients_factories.red_cash)} lang={lang} testId="sl-cf-red" />
                                    <SplitCard tone="emerald" label={lang === "ar" ? "علينا (أخضر / سالب)" : "We owe (green / negative)"}
                                        goldValue={fmt(displayLog.clients_factories.green_gold)} cashValue={fmt(displayLog.clients_factories.green_cash)} lang={lang} testId="sl-cf-green" />
                                </div>
                            </div>
                        ) : null}

                        {/* Employees list — MANAGER ONLY, only on the summary view (not drill-down) */}
                        {isManager && !selectedEmployeeId && data?.employees && data.employees.length > 0 ? (
                            <div className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid="sl-employees-section">
                                <div className="mb-3 flex items-center gap-2">
                                    <Users className="h-4 w-4 text-neutral-500" />
                                    <h2 className="font-heading text-sm font-bold text-neutral-800">
                                        {lang === "ar" ? `الموظفون (${data.employees.length})` : `Employees (${data.employees.length})`}
                                    </h2>
                                </div>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {data.employees.map((e) => (
                                        <button key={e.id} type="button" onClick={() => setSelectedEmployeeId(e.id)}
                                            data-testid={`sl-emp-${e.id}`}
                                            className="group flex items-center justify-between rounded-xl border border-neutral-200 bg-white p-3 text-right transition hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md">
                                            <div>
                                                <div className="font-heading text-sm font-bold text-neutral-800">{e.name}</div>
                                                <div className="mt-1 flex gap-3 text-[11px] text-neutral-500">
                                                    <span><Coins className="ms-0.5 inline h-3 w-3 text-amber-700" /> {fmt(e.current.gold)} {lang === "ar" ? "جم" : "g"}</span>
                                                    <span><Wallet className="ms-0.5 inline h-3 w-3 text-emerald-700" /> {fmt(e.current.cash)}</span>
                                                </div>
                                            </div>
                                            <ChevronLeft className="h-4 w-4 text-neutral-400 transition group-hover:-translate-x-1" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        <div className="rounded-xl bg-neutral-50 p-3 text-[11px] leading-relaxed text-neutral-500 ring-1 ring-neutral-100">
                            <Info className="ml-1 inline h-3 w-3" />
                            {lang === "ar"
                                ? "أرقام الرصيد (الذهب والكاش الحاليين) تمثل وضع المحل عند نهاية الفترة المحددة. أرقام الحركة (المباع والمشترى والمكسب) تخص الفترة المحددة فقط."
                                : "Balance values are point-in-time (end of the selected period). Movement values (sold/bought/profit) are for the selected period only."}
                        </div>
                    </div>
                )}
            </div>

            {askOpening ? (
                <OpeningBalancesModal
                    lang={lang}
                    onSaved={async () => {
                        setAskOpening(false);
                        if (refresh) await refresh();
                        await load();
                    }}
                />
            ) : null}
        </AppShell>
    );
}

function BalanceCard({ icon: Icon, tone, label, sub, value, unit, testId }) {
    const bg = { amber: "bg-amber-50 ring-amber-200", sky: "bg-sky-50 ring-sky-200", violet: "bg-violet-50 ring-violet-200" }[tone] || "bg-emerald-50 ring-emerald-200";
    const fg = { amber: "text-amber-900", sky: "text-sky-900", violet: "text-violet-900" }[tone] || "text-emerald-900";
    return (
        <div className={`rounded-2xl p-4 ring-1 ${bg}`} data-testid={testId}>
            <div className="flex items-center gap-2">
                <Icon className={`h-4 w-4 ${fg}`} />
                <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-600">{label}</div>
            </div>
            <div className={`mt-2 font-numbers text-3xl font-bold ${fg}`}>{value} <span className="text-sm font-normal">{unit}</span></div>
            <div className="mt-1 text-[10px] text-neutral-500">{sub}</div>
        </div>
    );
}

function FlowCard({ label, value, unit, icon: Icon, tone, testId }) {
    const bg = {
        rose: "bg-rose-50 ring-rose-200 text-rose-900",
        emerald: "bg-emerald-50 ring-emerald-200 text-emerald-900",
        amber: "bg-amber-50 ring-amber-200 text-amber-900",
    }[tone];
    return (
        <div className={`rounded-xl p-3 ring-1 ${bg}`} data-testid={testId}>
            <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider">
                <Icon className="h-3.5 w-3.5" /> {label}
            </div>
            <div className="mt-1 font-numbers text-xl font-bold">{value} <span className="text-xs font-normal">{unit}</span></div>
        </div>
    );
}

function SplitCard({ tone, label, goldValue, cashValue, lang, testId }) {
    const bg = tone === "rose" ? "bg-rose-50 ring-rose-200 text-rose-900" : "bg-emerald-50 ring-emerald-200 text-emerald-900";
    return (
        <div className={`rounded-xl p-3 ring-1 ${bg}`} data-testid={testId}>
            <div className="text-[11px] font-bold uppercase tracking-wider">{label}</div>
            <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                    <div className="text-[10px]">{lang === "ar" ? "ذهب (جم)" : "Gold (g)"}</div>
                    <div className="font-numbers text-lg font-bold" data-testid={`${testId}-gold`}>{goldValue}</div>
                </div>
                <div>
                    <div className="text-[10px]">{lang === "ar" ? "كاش" : "Cash"}</div>
                    <div className="font-numbers text-lg font-bold" data-testid={`${testId}-cash`}>{cashValue}</div>
                </div>
            </div>
        </div>
    );
}

function OpeningBalancesModal({ lang, onSaved }) {
    const [gold, setGold] = useState("");
    const [cash, setCash] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        const g = parseFloat(gold);
        const c = parseFloat(cash);
        if (!Number.isFinite(g) || g < 0) { toast.error(lang === "ar" ? "أدخل وزن الذهب الابتدائي" : "Enter opening gold weight"); return; }
        if (!Number.isFinite(c) || c < 0) { toast.error(lang === "ar" ? "أدخل الكاش الابتدائي" : "Enter opening cash"); return; }
        setBusy(true);
        try {
            await api.post("/shop/opening-balances", { opening_gold_grams: g, opening_cash: c });
            toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" data-testid="sl-opening-modal" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
                <div className="border-b border-neutral-200 px-5 py-4">
                    <div className="flex items-center gap-2">
                        <Store className="h-5 w-5 text-amber-700" />
                        <div className="font-heading text-lg font-bold">{lang === "ar" ? "الرصيد الابتدائي للمحل" : "Shop opening balances"}</div>
                    </div>
                    <p className="mt-1 text-xs text-neutral-500">
                        {lang === "ar" ? "قبل استخدام سجل المحل، أدخل بيانات البداية. هذه القيم تُدخل مرة واحدة." : "Before using the Shop Log, enter your starting values. Captured once."}
                    </p>
                </div>
                <div className="space-y-3 px-5 py-4">
                    <div>
                        <Label className="mb-1 block text-xs font-bold">{lang === "ar" ? "وزن الذهب الكامل الآن (جرام)" : "Total gold weight now (g)"}</Label>
                        <Input type="number" inputMode="decimal" step="0.001" value={gold} onChange={(e) => setGold(e.target.value)} data-testid="sl-opening-gold" />
                    </div>
                    <div>
                        <Label className="mb-1 block text-xs font-bold">{lang === "ar" ? "الكاش الحالي في المحل" : "Cash on hand"}</Label>
                        <Input type="number" inputMode="decimal" step="0.001" value={cash} onChange={(e) => setCash(e.target.value)} data-testid="sl-opening-cash" />
                    </div>
                </div>
                <div className="flex justify-end border-t border-neutral-200 bg-neutral-50 px-5 py-3">
                    <Button onClick={submit} disabled={busy || !gold || !cash} className="gap-2 bg-amber-700 hover:bg-amber-800" data-testid="sl-opening-save">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {lang === "ar" ? "حفظ" : "Save"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
